<template>
    <div class="picklist"
         :class="disabled && 'disabled'">
        <div class="available-items">
            <div
                class="header"
                v-if="availableLabel !== ''">
                {{ availableLabel }}
            </div>

            <b-list-group v-if="rows.length > 0">
                <div class="searchbar-wrapper">
                    <div
                        class="searchbar"
                        v-if="enableSearch">
                        <b-input-group class="searchbar-input-group">
                            <b-form-input
                                id="row-filter"
                                v-model="searchTerm"
                                placeholder="Zoeken"
                                type="text">
                            </b-form-input>

                            <template #append>
                                <b-button
                                    class="btn-times btn-clear-searchbar"
                                    @click="clearSearchTerm"
                                    :disabled="searchTerm === ''"
                                    variant="primary-icon">
                                </b-button>
                            </template>
                        </b-input-group>
                    </div>
                    <slot name="options"></slot>
                </div>


                <div
                    class="empty-items-list"
                    v-if="availableRows.length === 0">
                    <p>
                        {{
                            availableRows.length === 0 && searchTerm !== ''
                                ? 'Er zijn geen resultaten gevonden.'
                                : 'Alle items zijn geselecteerd.'
                        }}
                    </p>
                </div>

                <div
                    class="items-list"
                    v-else>
                    <b-list-group-item
                        v-for="row of availableRows"
                        :class="['available-item', row.pre_selected && 'active']"
                        :key="row[dataKey]"
                        @click="selectItem(row[dataKey])">
                        {{ row[nameKey] }}
                    </b-list-group-item>
                </div>
            </b-list-group>
        </div>

        <div class="controls">
            <b-button
                class="btn-select"
                title="selecteer"
                variant="primary"
                :disabled="!haveItemsSelected"
                @click="selectSelected">
                <i class="far fa-chevron-right"></i>
            </b-button>

            <b-button
                class="btn-select-all"
                title="selecteer alles"
                variant="primary"
                :disabled="availableRows.length === 0"
                @click="selectAll">
                <i class="far fa-chevron-double-right"></i>
            </b-button>

            <b-button
                class="btn-deselect"
                title="deselecteer"
                variant="primary"
                :disabled="!haveItemsDeselected"
                @click="removeSelected">
                <i class="far fa-chevron-left"></i>
            </b-button>

            <b-button
                class="btn-deselect-all"
                title="deselecteer alles"
                variant="primary"
                :disabled="selectedRows.length === 0"
                @click="removeAll">
                <i class="far fa-chevron-double-left"></i>
            </b-button>
        </div>

        <div class="selected-items">
            <div
                class="header"
                v-if="selectedLabel !== ''">
                {{ selectedLabel }}
            </div>

            <b-list-group>
                <div
                    class="empty-items-list"
                    v-if="selectedRows.length === 0">
                    <p>Er zijn geen items geselecteerd.</p>
                </div>

                <div
                    class="list-items"
                    v-else>
                    <b-list-group-item
                        v-for="row of selectedRows"
                        :class="['available-item', row.pre_selected && 'active']"
                        :key="row[dataKey]"
                        @click="deselectItem(row[dataKey])">
                        {{ row[nameKey] }}
                    </b-list-group-item>
                </div>
            </b-list-group>
        </div>
    </div>
</template>

<script>
    export default {
        name: 'design-2021-apprentice-picklist-component',
        props: {
            dataKey: {
                type: String,
                required: true
            },
            nameKey: {
                type: String,
                required: false,
                default: 'name'
            },
            value: {
                type: Array,
                required: false
            },
            availableLabel: {
                type: String,
                required: false,
                default: ''
            },
            selectedLabel: {
                type: String,
                required: false,
                default: ''
            },
            enableSearch: {
                type: Boolean,
                required: false,
                default: false
            },
            disabled: {
                type: Boolean,
                required: false,
                default: false
            }
        },
        data() {
            return {
                rows:
                    this.value.length > 0
                        ? this.value.map((row) => {
                            return {
                                ...row,
                                pre_selected: false,
                                selected: row?.selected ? row.selected : false
                            };
                        })
                        : [],
                searchTerm: ''
            };
        },

        methods: {
            selectSelected() {
                this.rows = this.rows.map((row) => {
                    if (row.pre_selected) {
                        row.selected = true;
                        row.pre_selected = false;
                    }

                    return row;
                });

                this.update();
            },

            selectAll() {
                this.rows = this.rows.map((row) => {
                    row.selected = true;
                    row.pre_selected = false;
                    return row;
                });

                this.update();
            },

            removeSelected() {
                this.rows = this.rows.map((row) => {
                    if (row.pre_selected && row.selected) {
                        row.selected = false;
                        row.pre_selected = false;
                    }
                    return row;
                });

                this.update();
            },

            removeAll() {
                this.rows = this.rows.map((row) => {
                    row.selected = false;
                    row.pre_selected = false;
                    return row;
                });

                this.update();
            },

            selectItem(rowKeyValue) {
                let item = this.rows.find((row) => row[this.dataKey] === rowKeyValue);
                item.pre_selected = !item.pre_selected;
            },

            deselectItem(rowId) {
                let item = this.rows.find((row) => row[this.dataKey] === rowId);
                item.pre_selected = !item.pre_selected;
            },

            update() {
                this.$emit(
                    'update',
                    this.rows.filter((row) => row.selected)
                );
            },

            clearSearchTerm() {
                this.searchTerm = '';
            }
        },

        computed: {
            haveItemsSelected() {
                if (this.rows.length > 0) {
                    for (const row of this.rows) {
                        if (row.pre_selected && !row.selected) {
                            return true;
                        }
                    }
                }

                return false;
            },

            haveItemsDeselected() {
                if (this.rows.length > 0) {
                    for (const row of this.rows) {
                        if (row.pre_selected && row.selected) {
                            return true;
                        }
                    }
                }
            },

            availableRows() {
                let rows = [...this.rows];
                rows = rows.filter((row) => !row.selected);

                if (this.searchTerm === '') {
                    return rows;
                } else if (this.searchTerm.length >= 3) {
                    return rows.filter((row) => row[this.nameKey].toLowerCase().includes(this.searchTerm.toLowerCase()));
                }

                return rows;
            },

            selectedRows() {
                if (this.rows.length === 0) {
                    return [];
                }

                return this.rows.filter((row) => row.selected);
            }
        },

        watch: {
            value: {
                handler(newVal) {
                    this.rows = newVal.length > 0
                        ? newVal.map((row) => {
                            return {
                                ...row,
                                pre_selected: false,
                                selected: row?.selected ? row.selected : false
                            };
                        })
                        : [];
                },
                deep: true,
            }
        }
    };
</script>

<style scoped lang="scss">
    .picklist {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: flex-start;

        &.disabled ::v-deep{
            pointer-events: none;

            input.form-control {
                border: 1px solid rgba($black, 10%)
            }

            input.form-control,
            .list-group-item,
            .available-items .list-group,
            .selected-items .list-group {
                background-color: $disabled-color;
            }

            input.form-control,
            .list-group-item,
            .empty-items-list {
                opacity: 0.4;
            }

            button {
                background-color: $disabled-color;
                border: 1px solid rgba($black, 10%)
            }
        }
    }

    .available-items,
    .selected-items {
        width: min(100%, 450px);

        .header {
            font-family: $headings-font-family;
            font-weight: $headings-font-weight;
            font-size: 1.125rem;
            margin-bottom: map-get($grid-gutter-spacing, 'ten');
        }

        .list-group,
        .empty-items-list {
            height: 300px;
        }

        .list-group {
            border: 1px solid $border-color;
            border-radius: 12px;
            padding: map-get($grid-gutter-spacing, 'eight');
        }

        .items-list {
            height: 300px;
            overflow-y: auto;
            overflow-x: hidden;
        }

        .list-group-item {
            border: none;
            padding: map-get($grid-gutter-spacing, 'eight');
            border-radius: 0;

            &:hover {
                cursor: pointer;
                background-color: $primary;
                color: $white;
            }
        }

        .empty-items-list {
            p {
                padding: map-get($grid-gutter-spacing, 'eight');
                line-height: 1.5;
            }
        }
    }

    .controls {
        display: flex;
        column-gap: map-get($grid-gutter-spacing, 'four');
        margin: map-get($grid-gutter-spacing, 'ten') 0 0;

        .btn {
            font-size: $default-font-size;
            padding: 6px 10px;
        }

        .btn-select {
            i:before {
                content: '\f078';
            }
        }

        .btn-select-all {
            i:before {
                content: '\f322';
            }
        }

        .btn-deselect {
            i:before {
                content: '\f077';
            }
        }

        .btn-deselect-all {
            i:before {
                content: '\f325';
            }
        }
    }

    .searchbar-wrapper {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: map-get($grid-gutter-spacing, 'ten') map-get($grid-gutter-spacing, 'fifteen');
        margin-bottom: map-get($grid-gutter-spacing, 'fifteen');
        padding-bottom: map-get($grid-gutter-spacing, 'fifteen');
        border-bottom: 1px solid $border-color;
    }

    .searchbar {
        flex: 1;

        input {
            &:focus {
                outline: none;
                box-shadow: none;
            }
        }

        .searchbar-input-group {
            &:focus-within {
                box-shadow: 0 0 0 0.2rem rgba($primary, 25%);
                border-radius: $border-radius-content;
            }
        }

        .btn-clear-searchbar {
            color: $white;
            border-top-right-radius: $border-radius-content;
            border-bottom-right-radius: $border-radius-content;
            background-color: var(--primaryLighter);
            font-size: $base-font-size;

            &:before {
                margin-right: 0;
            }
        }

        &:hover {
            .btn-clear-searchbar {
                background-color: var(--primary);
            }
        }
    }

    @media (min-width: map-get($grid-breakpoints, 'lg')) {
        .picklist {
            flex-direction: row;
        }

        .controls {
            display: flex;
            flex-direction: column;
            row-gap: map-get($grid-gutter-spacing, 'ten');
            padding: 0 map-get($grid-gutter-spacing, 'fifteen');

            .btn-select {
                i:before {
                    content: '\f054';
                }
            }

            .btn-select-all {
                i:before {
                    content: '\f324';
                }
            }

            .btn-deselect {
                i:before {
                    content: '\f053';
                }
            }

            .btn-deselect-all {
                i:before {
                    content: '\f323';
                }
            }
        }
    }
</style>
